import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { LinkItem } from '@occc/ui-design-system';
import { useAppLayoutContextConsumer } from '@occc-app/design-system';
import { Offer_Engagement_Type, StoredValueProgramCreateMutation } from '@occc/graphql';
import { OfferType } from '@occc-app/offerValidationService/types';
import { useChainId } from '@occc-app/Chain/hooks';
import { CampaignComponentTabs } from '@occc-app/routes/CampaignComponents/components/ConfigurationLayout';
import { routeCreator as programManagementRouteCreator } from '@occc-app/routes/ProgramManagement/utils';
import { OfferCreateConfigurePage } from '@occc-app/routes/Offer/OfferCreatePage/OfferCreateConfigurePage';
import { Flow_Types, OfferConfigureOperations } from '@occc-app/offerHelpers/types';
import { OperationResult } from '@occc-app/offerHelpers/operations/runOperations';

import { routeCreator } from '../stored-value.utils';
import {
  getStoredValueConfigureFormOperations,
  StoredValueConfigureForm,
  transformStoredValueOfferToInitialValues,
  StoredValueConfigureFormInitialValues,
  StoredValueConfigureFormValues,
} from '../stored-value-configure-form';

const PAGE_TITLE = 'New Stored Value Program';

const getStoredValueCreatePageLinks = (): LinkItem[] => [
  {
    to: programManagementRouteCreator.base(),
    label: 'Program Management',
  },
  {
    to: routeCreator.list,
    label: 'Stored Value Programs',
  },
  {
    label: 'New Program',
  },
];

export const StoredValueCreatePageContainer = () => {
  const navigate = useNavigate();
  const chainId = useChainId();

  const links = useMemo(() => getStoredValueCreatePageLinks(), []);
  useAppLayoutContextConsumer(links);

  const configureOperationsCompleted = useCallback(
    (operationResults: OperationResult[]) => {
      const operationName: OfferConfigureOperations = 'storedValueProgramCreate';
      const operationResult = operationResults.find((r) => r.name === operationName)?.result
        .data as StoredValueProgramCreateMutation | undefined;

      const programId = operationResult?.storedValueProgramCreate?.id;
      if (programId) {
        navigate(`${routeCreator.update(programId)}/${CampaignComponentTabs.Target}`);
      } else {
        navigate(routeCreator.list);
      }
    },
    [navigate],
  );

  return (
    <OfferCreateConfigurePage<StoredValueConfigureFormValues, StoredValueConfigureFormInitialValues>
      pageTitle={PAGE_TITLE}
      offerType={OfferType.StoredValue}
      engagementType={Offer_Engagement_Type.StoredValue}
      getConfigureOperations={getStoredValueConfigureFormOperations}
      getInitialValues={() => transformStoredValueOfferToInitialValues(undefined, chainId)}
      onConfigureOperationsCompleted={configureOperationsCompleted}
      renderConfigureForm={({ onSubmit, initialValues, loading, executing }) => (
        <StoredValueConfigureForm
          onSubmit={onSubmit}
          initialValues={initialValues}
          loading={loading || executing}
          onCancelClick={() => navigate(routeCreator.list)}
          currentFlowType={Flow_Types.Configure}
          isFormEditable
        />
      )}
    />
  );
};
